import { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Popover, Stack } from '@mui/material';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import { ROUTES } from '@/config/routes';
import { queryKeys } from '@/react-query/constants';
import AppButton from '@/shared/ui/AppButton';

import useNotificationsService, {
  optimisticallyMarkAllNotificationsAsRead,
  restoreNotificationsCacheSnapshot,
} from '../../api/notifications.service';
import useNotificationsStore from '../../model/notifications';

interface Props {
  closeDrawer: () => void;
}

const dropdownItemSx = {
  px: '0.5rem',
  py: '0.25rem',
  borderRadius: '0.5rem',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '0.625rem',
  bgcolor: 'transparent',
  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
  color: '#807D93',
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.6,
  whiteSpace: 'nowrap',
  minHeight: 'unset',
} as const;

const iconPillSx = {
  display: 'flex',
  height: '1.875rem',
  padding: '0.5rem 0.75rem',
  alignItems: 'center',
  gap: '0.6875rem',
  borderRadius: '0.75rem',
  bgcolor: '#f5f5f7',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': { bgcolor: '#ebeaef' },
} as const;

const NotificationsListHeader: FC<Props> = ({ closeDrawer }) => {
  const { t } = useTranslation();
  const { dnd, selectedDndOption, isDrawerExpanded, showReadNotifications } = useNotificationsStore(
    useShallow((state) => ({
      dnd: state.dnd,
      selectedDndOption: state.selectedDndOption,
      isDrawerExpanded: state.isDrawerExpanded,
      showReadNotifications: state.showReadNotifications,
    })),
  );
  const setDnd = useNotificationsStore((state) => state.setDnd);
  const toggleDrawerExpanded = useNotificationsStore((state) => state.toggleDrawerExpanded);
  const toggleShowReadNotifications = useNotificationsStore(
    (state) => state.toggleShowReadNotifications,
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { markAllNotificationsAsRead } = useNotificationsService();

  const [dndAnchorEl, setDndAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);

  const handleSettingsMenuClose = () => {
    setSettingsAnchorEl(null);
  };

  const { mutate: markAllAsRead } = useMutation(() => markAllNotificationsAsRead(), {
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [queryKeys.notifications] });
      await queryClient.cancelQueries({ queryKey: [queryKeys.notificationsCount] });
      await queryClient.cancelQueries({ queryKey: [queryKeys.notificationsCountBreakdown] });

      const snapshot = optimisticallyMarkAllNotificationsAsRead(queryClient);
      handleSettingsMenuClose();
      return snapshot;
    },
    onError: (_error, _variables, snapshot) => {
      restoreNotificationsCacheSnapshot(queryClient, snapshot);
    },
    onSuccess: () => {
      queryClient.setQueryData([queryKeys.notificationsCount], 0);
      queryClient.removeQueries({ queryKey: [queryKeys.notifications, 'drawer'] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.notificationsCount] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.notificationsCountBreakdown] });
    },
  });

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ px: '1rem', py: '0.75rem' }}
    >
      <AppButton
        variant="secondary"
        size="small"
        data-testid="notifications-dnd-button"
        onClick={(e: MouseEvent<HTMLElement>) => setDndAnchorEl(e.currentTarget)}
        startIcon={
          dnd ? (
            <svg
              data-testid="notifications-dnd-active-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M8.72 13.7508C8.85801 13.5118 9.16386 13.4301 9.40295 13.5679C9.64209 13.7059 9.72397 14.0117 9.58589 14.2508C9.425 14.5294 9.19393 14.7611 8.91532 14.922C8.63674 15.0828 8.32029 15.1674 7.99865 15.1675C7.67706 15.1674 7.36052 15.0828 7.08198 14.922C6.80344 14.7611 6.57161 14.5294 6.41076 14.2508C6.27293 14.0118 6.35478 13.7059 6.5937 13.5679C6.83275 13.4298 7.13848 13.5119 7.27664 13.7508C7.34973 13.8774 7.45545 13.9824 7.58198 14.0555C7.70855 14.1285 7.85251 14.1674 7.99865 14.1675C8.14477 14.1674 8.28876 14.1285 8.41532 14.0555C8.54183 13.9824 8.64693 13.8773 8.72 13.7508Z"
                fill="currentColor"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.978468 0.980622C1.17373 0.785462 1.49027 0.785412 1.6855 0.980622L15.0188 14.314C15.2141 14.5092 15.214 14.8257 15.0188 15.021C14.8236 15.2162 14.5071 15.2162 14.3118 15.021L11.125 11.8341H2.66532C2.4394 11.8341 2.21796 11.7686 2.0286 11.6453C1.8392 11.522 1.68971 11.3462 1.59826 11.1395C1.50686 10.9328 1.47746 10.7037 1.51362 10.4806C1.54989 10.2575 1.65059 10.0495 1.80269 9.88231L1.8131 9.8706C2.2567 9.41332 2.65941 8.9937 2.9661 8.34455C3.27218 7.69645 3.49864 6.78102 3.49865 5.33414C3.49858 4.99355 3.53744 4.65436 3.61388 4.32307L0.978468 1.68765C0.783226 1.49239 0.783212 1.17588 0.978468 0.980622ZM4.49865 5.33414C4.49864 6.88565 4.25566 7.95589 3.87039 8.77164C3.48876 9.57941 2.98195 10.101 2.54162 10.5548C2.51996 10.5787 2.50581 10.609 2.5006 10.6408C2.49548 10.6725 2.50002 10.7051 2.51297 10.7345C2.526 10.764 2.54723 10.7892 2.57417 10.8068C2.6012 10.8244 2.63308 10.8341 2.66532 10.8341H10.125L4.50256 5.21174C4.50112 5.25253 4.49864 5.29328 4.49865 5.33414Z"
                fill="currentColor"
              />
              <path
                d="M7.7786 0.839346C8.59236 0.799551 9.40191 0.981893 10.1204 1.36604C10.8388 1.7502 11.4399 2.32219 11.8587 3.02099C12.2774 3.71977 12.4986 4.5195 12.4986 5.33414C12.4986 7.02316 12.981 8.2379 13.552 9.08935C13.7056 9.31855 13.6443 9.62886 13.4153 9.78271C13.186 9.93652 12.8751 9.87532 12.7213 9.64599C12.0432 8.63481 11.4986 7.22741 11.4986 5.33414C11.4986 4.70058 11.3263 4.07878 11.0006 3.53531C10.6749 2.99181 10.2078 2.54634 9.64904 2.24755C9.09023 1.94878 8.46034 1.80709 7.82743 1.83804C7.19458 1.86903 6.58158 2.07149 6.05464 2.42333C5.82501 2.57654 5.51459 2.5149 5.36128 2.28531C5.20819 2.05569 5.27041 1.74523 5.49995 1.59195C6.17744 1.13962 6.96496 0.879176 7.7786 0.839346Z"
                fill="currentColor"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M8.7207 13.7507C8.85882 13.5116 9.16457 13.4297 9.40365 13.5677C9.64258 13.7058 9.72451 14.0116 9.58659 14.2507C9.42575 14.5292 9.19452 14.761 8.91602 14.9219C8.63749 15.0827 8.32095 15.1672 7.99935 15.1673C7.67773 15.1673 7.36126 15.0826 7.08268 14.9219C6.80403 14.761 6.57237 14.5293 6.41146 14.2507C6.27338 14.0115 6.35527 13.7058 6.5944 13.5677C6.8335 13.4298 7.1393 13.5116 7.27734 13.7507C7.35048 13.8773 7.45604 13.9822 7.58268 14.0553C7.70929 14.1284 7.85318 14.1673 7.99935 14.1673C8.14542 14.1672 8.2895 14.1284 8.41602 14.0553C8.54242 13.9822 8.64767 13.8771 8.7207 13.7507Z"
                fill="currentColor"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.9987 0.833984C9.19205 0.834049 10.3371 1.30789 11.181 2.15169C12.0248 2.99559 12.4987 4.14057 12.4987 5.33398C12.4987 6.78133 12.7257 7.6969 13.0319 8.34505C13.3386 8.99404 13.7407 9.4132 14.1842 9.87044C14.1879 9.87422 14.1918 9.87827 14.1953 9.88216C14.3474 10.0494 14.4475 10.2573 14.4837 10.4805C14.5199 10.7036 14.4905 10.9326 14.3991 11.1393C14.3076 11.346 14.1581 11.5219 13.9687 11.6452C13.8031 11.753 13.6129 11.8165 13.4167 11.8307L13.332 11.834H2.66536C2.43939 11.8338 2.21794 11.7679 2.02865 11.6445C1.8393 11.521 1.68953 11.3449 1.59831 11.138C1.50712 10.9312 1.47851 10.7023 1.51497 10.4792C1.55149 10.256 1.65164 10.0479 1.80404 9.88086L1.81445 9.86979C2.25718 9.41272 2.6595 8.99354 2.96615 8.3444C3.27221 7.69631 3.4987 6.78095 3.4987 5.33398C3.4987 4.14051 3.97314 2.99561 4.81706 2.15169C5.6609 1.30805 6.80545 0.834028 7.9987 0.833984ZM7.9987 1.83398C7.07056 1.83403 6.18042 2.20311 5.52409 2.85937C4.86776 3.51574 4.4987 4.40577 4.4987 5.33398C4.4987 6.88587 4.25506 7.9558 3.86979 8.77148C3.48811 9.57947 2.98232 10.1011 2.54297 10.5547L2.51628 10.5944C2.50923 10.6089 2.50393 10.6246 2.5013 10.6406C2.49615 10.6724 2.50071 10.7049 2.51367 10.7344C2.52668 10.7638 2.54792 10.789 2.57487 10.8066C2.60189 10.8242 2.63378 10.834 2.66602 10.834H13.3327C13.3647 10.8339 13.3963 10.8247 13.4232 10.8073C13.4501 10.7897 13.472 10.7644 13.485 10.735C13.4981 10.7055 13.5019 10.6725 13.4967 10.6406C13.4916 10.6089 13.4773 10.5792 13.4557 10.5553C13.0155 10.1015 12.5092 9.57976 12.1276 8.77213C11.7422 7.95629 11.4987 6.88597 11.4987 5.33398C11.4987 4.4059 11.1301 3.51572 10.474 2.85937C9.81764 2.20305 8.92687 1.83405 7.9987 1.83398Z"
                fill="currentColor"
              />
              <path
                d="M2.25716 1.04557C2.41645 0.820124 2.72824 0.766577 2.95378 0.925781C3.17915 1.08509 3.23275 1.39688 3.07357 1.6224C2.32085 2.68884 1.83203 3.92361 1.83203 5.33398C1.83193 5.61004 1.60811 5.83398 1.33203 5.83398C1.05595 5.83398 0.832129 5.61004 0.832031 5.33398C0.832031 3.67788 1.41004 2.2457 2.25716 1.04557Z"
                fill="currentColor"
              />
              <path
                d="M13.0436 0.925781C13.2692 0.766577 13.5809 0.820124 13.7402 1.04557C14.5873 2.2457 15.1654 3.67788 15.1654 5.33398C15.1653 5.61004 14.9414 5.83397 14.6654 5.83398C14.3893 5.83398 14.1655 5.61004 14.1654 5.33398C14.1654 3.92362 13.6765 2.68884 12.9238 1.6224C12.7646 1.39688 12.8182 1.08508 13.0436 0.925781Z"
                fill="currentColor"
              />
            </svg>
          )
        }
      >
        {dnd
          ? selectedDndOption === 'hour'
            ? t('for_1h')
            : t('until_tomorrow')
          : t('pause_notifications', { defaultValue: 'Pause notifications' })}
      </AppButton>

      <Stack direction="row" gap="0.5rem" alignItems="center">
        <Box
          sx={iconPillSx}
          onClick={toggleDrawerExpanded}
          data-testid="notifications-expand-button"
        >
          {isDrawerExpanded ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 22 22"
              fill="none"
            >
              <path
                d="M19.3128 1.71495C19.5813 1.44652 20.0165 1.44664 20.285 1.71495C20.5535 1.98343 20.5535 2.41863 20.285 2.68711L14.0241 8.94802H18.7372C19.1169 8.94803 19.4247 9.25583 19.4247 9.63552C19.4247 10.0152 19.1169 10.323 18.7372 10.323H12.3644C11.9849 10.3228 11.6769 10.0151 11.6769 9.63552V3.26272C11.6771 2.88332 11.985 2.57541 12.3644 2.57522C12.744 2.57522 13.0517 2.8832 13.0519 3.26272V7.97585L19.3128 1.71495ZM10.3225 18.7386C10.3225 19.1183 10.0147 19.4261 9.63503 19.4261C9.25533 19.4261 8.94753 19.1183 8.94753 18.7386V14.0264L2.68663 20.2873C2.41819 20.5555 1.9829 20.5555 1.71446 20.2873C1.44603 20.0189 1.44615 19.5836 1.71446 19.3151L7.97536 13.0542H3.26223C2.88283 13.054 2.57492 12.7461 2.57473 12.3667C2.57473 11.9872 2.88271 11.6794 3.26223 11.6792H9.63503C10.0147 11.6792 10.3225 11.987 10.3225 12.3667V18.7386Z"
                fill="#807D93"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M8.52246 10.5931C8.76648 10.3491 9.16216 10.3492 9.40625 10.5931C9.65032 10.8372 9.65034 11.2328 9.40625 11.4769L4.84082 16.0423H8.16032C8.50532 16.0425 8.78532 16.3223 8.78532 16.6673C8.78532 17.0124 8.50532 17.2921 8.16032 17.2923H3.33203C2.98685 17.2923 2.70703 17.0125 2.70703 16.6673V11.8398C2.70703 11.4947 2.98685 11.2148 3.33203 11.2148C3.67721 11.2148 3.95703 11.4947 3.95703 11.8398V15.1585L8.52246 10.5931ZM17.2904 8.16146C17.2904 8.50664 17.0105 8.78646 16.6654 8.78646C16.3202 8.78646 16.0404 8.50664 16.0404 8.16146V4.84277L11.4749 9.4082C11.2309 9.65221 10.8352 9.65209 10.5911 9.4082C10.3471 9.16412 10.3471 8.76848 10.5911 8.52441L15.1566 3.95898H11.8371C11.4921 3.95878 11.2121 3.67903 11.2121 3.33398C11.2121 2.98893 11.4921 2.70919 11.8371 2.70898H16.6654C17.0105 2.70898 17.2904 2.98881 17.2904 3.33398V8.16146Z"
                fill="#807D93"
              />
            </svg>
          )}
        </Box>

        <Box
          sx={iconPillSx}
          onClick={(e: MouseEvent<HTMLElement>) => setSettingsAnchorEl(e.currentTarget)}
          data-testid="notifications-settings-button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M7.99561 3.44895C8.50683 1.35035 11.4932 1.35035 12.0044 3.44895L12.0093 3.47011C12.0407 3.5868 12.0966 3.69556 12.1753 3.78749C12.2584 3.88453 12.3633 3.96078 12.4813 4.00966C12.5993 4.05854 12.7274 4.07817 12.8548 4.06825C12.9822 4.05834 13.1055 4.01844 13.2145 3.95188C15.0026 2.86237 17.041 4.81043 16.1442 6.61139L16.0482 6.78554C15.9816 6.89456 15.9417 7.01791 15.9318 7.14524C15.9219 7.27261 15.9423 7.40074 15.9912 7.51878C16.0401 7.63675 16.1164 7.74172 16.2134 7.82476C16.3103 7.90769 16.4255 7.96641 16.5495 7.99648C18.651 8.50536 18.6488 11.4931 16.5495 12.0028C16.4255 12.0331 16.3103 12.0931 16.2134 12.1762C16.1165 12.2593 16.0401 12.3642 15.9912 12.4822C15.9424 12.6001 15.9228 12.7284 15.9326 12.8557C15.9425 12.9829 15.9818 13.1064 16.0482 13.2154C17.1726 15.0612 15.0602 17.1729 13.2145 16.0482C13.1055 15.9819 12.9821 15.9425 12.8548 15.9327C12.7276 15.9229 12.6 15.9433 12.4821 15.9921C12.364 16.041 12.2584 16.1164 12.1753 16.2134C12.0922 16.3105 12.0338 16.4262 12.0036 16.5504C11.4942 18.6511 8.50607 18.6486 7.99642 16.5495C7.96636 16.4256 7.90761 16.3104 7.82471 16.2134C7.74169 16.1164 7.63666 16.0402 7.51872 15.9913C7.40068 15.9424 7.27255 15.922 7.14518 15.9319C7.04973 15.9393 6.9567 15.9638 6.87012 16.0035L6.78548 16.0482C4.93976 17.1729 2.82739 15.0612 3.95182 13.2154V13.2146C4.01838 13.1055 4.05828 12.9822 4.0682 12.8549C4.07809 12.7275 4.05766 12.5994 4.00879 12.4813C3.95991 12.3634 3.88359 12.2583 3.78662 12.1754C3.68966 12.0925 3.5745 12.0337 3.45052 12.0036C1.41473 11.5107 1.35273 8.69109 3.25928 8.05182L3.45052 7.99648C3.98407 7.86639 4.23746 7.25438 3.95182 6.78554C2.82707 4.93968 4.93969 2.8272 6.78548 3.95188C6.89441 4.01816 7.01724 4.0584 7.14437 4.06825C7.27168 4.07808 7.39995 4.05774 7.5179 4.00885C7.63588 3.95993 7.74088 3.88373 7.82389 3.78668C7.90691 3.68962 7.96556 3.5739 7.99561 3.44976V3.44895ZM10.7821 3.71832C10.5621 2.91874 9.40973 2.92777 9.21061 3.74518C9.1342 4.06013 8.98426 4.35255 8.7736 4.59885C8.56267 4.84546 8.29618 5.03851 7.99642 5.16282C7.6967 5.28709 7.37184 5.33914 7.04834 5.31419C6.72486 5.2892 6.41153 5.18837 6.13444 5.01959C5.40773 4.57692 4.57695 5.40789 5.01953 6.1345C5.72346 7.28945 5.14034 8.78527 3.87044 9.1773L3.74512 9.21148C2.91765 9.41238 2.91927 10.5885 3.7443 10.7886H3.74512C4.06048 10.8651 4.35308 11.0154 4.59961 11.2265C4.8462 11.4375 5.03939 11.7037 5.16358 12.0036C5.28772 12.3035 5.33927 12.6282 5.31413 12.9517C5.28896 13.2751 5.18759 13.5879 5.01872 13.8648C4.57593 14.5915 5.40766 15.4234 6.13444 14.9805C6.41144 14.8116 6.72485 14.7111 7.04834 14.6859C7.37187 14.6608 7.69659 14.7124 7.99642 14.8365C8.29634 14.9607 8.56333 15.1538 8.77441 15.4004C8.98529 15.6469 9.135 15.9397 9.21143 16.2549L9.2334 16.3298C9.4851 17.0814 10.5947 17.0551 10.7886 16.2558V16.2549C10.8652 15.9397 11.0153 15.6468 11.2264 15.4004C11.4375 15.1541 11.7038 14.9606 12.0036 14.8365C12.3033 14.7124 12.6282 14.6609 12.9517 14.6859C13.2348 14.7078 13.5097 14.7883 13.7598 14.9211L13.8656 14.9805L13.9339 15.0188C14.643 15.3717 15.4094 14.5695 14.9805 13.8656C14.8117 13.5885 14.7109 13.2752 14.6859 12.9517C14.6608 12.6283 14.7123 12.3034 14.8364 12.0036C14.9606 11.7039 15.154 11.4375 15.4004 11.2265C15.6468 11.0154 15.9397 10.8653 16.2549 10.7886C17.0824 10.5877 17.0808 9.4116 16.2557 9.21148H16.2549C15.9396 9.13505 15.6469 8.98537 15.4004 8.77447C15.1539 8.56349 14.9606 8.29703 14.8364 7.99729C14.7122 7.69738 14.6607 7.37203 14.6859 7.0484C14.7111 6.72485 14.8115 6.41153 14.9805 6.1345C15.4092 5.43065 14.6429 4.62855 13.9339 4.98134L13.8656 5.01959C13.5885 5.18859 13.2752 5.28982 12.9517 5.315C12.6282 5.34014 12.3033 5.2885 12.0036 5.16445C11.7037 5.04026 11.4367 4.84707 11.2256 4.60048C11.0145 4.35391 10.865 4.06061 10.7886 3.74518L10.7821 3.71832ZM11.0417 10.0001C11.0417 9.42476 10.5753 8.95839 10 8.95839C9.4247 8.95839 8.95833 9.42476 8.95833 10.0001C8.95833 10.5754 9.4247 11.0417 10 11.0417C10.5753 11.0417 11.0417 10.5754 11.0417 10.0001ZM12.2917 10.0001C12.2917 11.2657 11.2657 12.2917 10 12.2917C8.73435 12.2917 7.70833 11.2657 7.70833 10.0001C7.70833 8.73441 8.73435 7.70839 10 7.70839C11.2657 7.70839 12.2917 8.73441 12.2917 10.0001Z"
              fill="#807D93"
            />
          </svg>
        </Box>

        <Box sx={iconPillSx} onClick={closeDrawer} data-testid="notifications-drawer-close">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M14.5581 4.55806C14.8021 4.31398 15.1978 4.31398 15.4418 4.55806C15.6859 4.80214 15.6859 5.19777 15.4418 5.44185L10.8837 9.99995L15.4418 14.5581C15.6859 14.8021 15.6859 15.1978 15.4418 15.4418C15.1978 15.6859 14.8021 15.6859 14.5581 15.4418L9.99995 10.8837L5.44185 15.4418C5.19777 15.6859 4.80214 15.6859 4.55806 15.4418C4.31398 15.1978 4.31398 14.8021 4.55806 14.5581L9.11616 9.99995L4.55806 5.44185C4.31398 5.19777 4.31398 4.80214 4.55806 4.55806C4.80214 4.31398 5.19777 4.31398 5.44185 4.55806L9.99995 9.11616L14.5581 4.55806Z"
              fill="#807D93"
            />
          </svg>
        </Box>
      </Stack>

      <Popover
        open={Boolean(dndAnchorEl)}
        anchorEl={dndAnchorEl}
        onClose={() => setDndAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        PaperProps={{
          elevation: 0,
          sx: {
            mt: '0.25rem',
            borderRadius: '0.5rem',
            bgcolor: '#fff',
            boxShadow: '0px 1px 8px 0px rgba(0,0,0,0.15)',
            p: '0.5rem !important',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.125rem',
            minWidth: dndAnchorEl?.offsetWidth,
          },
        }}
      >
        {[
          ...(selectedDndOption !== 'hour'
            ? [
                {
                  key: 'hour',
                  label: t('for_1h'),
                  onClick: () => {
                    setDnd('hour', dayjs().add(1, 'hour').format('YYYY-MM-DD HH:mm:ss'));
                    setDndAnchorEl(null);
                  },
                },
              ]
            : []),
          ...(selectedDndOption !== 'tomorrow'
            ? [
                {
                  key: 'tomorrow',
                  label: t('until_tomorrow'),
                  onClick: () => {
                    setDnd('tomorrow', dayjs().add(24, 'hour').format('YYYY-MM-DD HH:mm:ss'));
                    setDndAnchorEl(null);
                  },
                },
              ]
            : []),
          ...(dnd
            ? [
                {
                  key: 'unmute',
                  label: t('Unmute notifications'),
                  onClick: () => {
                    setDnd(undefined);
                    setDndAnchorEl(null);
                  },
                },
              ]
            : []),
        ].map(({ key, label, onClick }) => (
          <Box
            key={key}
            onClick={onClick}
            sx={dropdownItemSx}
            data-testid={`notifications-dnd-${key}`}
          >
            {label}
          </Box>
        ))}
      </Popover>

      <Popover
        open={Boolean(settingsAnchorEl)}
        anchorEl={settingsAnchorEl}
        onClose={() => setSettingsAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableRestoreFocus
        sx={{ '& .MuiPopover-paper': { marginLeft: '2.375rem' } }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: '0.25rem',
            borderRadius: '0.5rem',
            bgcolor: '#fff',
            boxShadow: '0px 1px 8px 0px rgba(0,0,0,0.15)',
            p: '0.5rem !important',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.125rem',
            minWidth: '11.0625rem',
          },
        }}
      >
        {!showReadNotifications && (
          <Box
            onClick={() => markAllAsRead()}
            sx={dropdownItemSx}
            data-testid="notifications-mark-all-read"
          >
            {t('mark_all_as_read')}
          </Box>
        )}
        <Box
          onClick={() => {
            toggleShowReadNotifications();
            queryClient.removeQueries({ queryKey: [queryKeys.notifications, 'drawer'] });
            queryClient.invalidateQueries({ queryKey: [queryKeys.notificationsCountBreakdown] });
            queryClient.invalidateQueries({ queryKey: [queryKeys.notificationsCount] });
            setSettingsAnchorEl(null);
          }}
          sx={dropdownItemSx}
        >
          {showReadNotifications
            ? t('hide_read_notifications', { defaultValue: 'Hide read notifications' })
            : t('include_read_notifications', { defaultValue: 'Include read notifications' })}
        </Box>
        <Box
          onClick={() => {
            navigate(`${ROUTES.AUTHORIZED.USER_PROFILE}/notifications`);
            setSettingsAnchorEl(null);
            closeDrawer();
          }}
          sx={dropdownItemSx}
          data-testid="notifications-open-settings"
        >
          {t('settings')}
        </Box>
      </Popover>
    </Stack>
  );
};

export default NotificationsListHeader;
